
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAffiliateSystem } from "@/hooks/useAffiliateSystem";
import { Copy, DollarSign, Users, TrendingUp, Calculator, MessageCircle, ExternalLink, User } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

interface ReferralUser {
  customer_id: string;
  customer_name: string;
  customer_email: string;
  customer_whatsapp: string;
  customer_plan: string;
  customer_is_subscribed: boolean;
  sale_amount: number;
  commission_amount: number;
  status: string;
  created_at: string;
}

const AffiliatePanel = () => {
  const { affiliateCode, sales, isLoading, createAffiliateCode } = useAffiliateSystem();
  const { user } = useAuth();
  
  // Estados para a calculadora
  const [calcSales, setCalcSales] = useState(1);
  const [calcPlan, setCalcPlan] = useState("mensal");
  const [referrals, setReferrals] = useState<ReferralUser[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  
  const planPrices = {
    mensal: 24.90,
    trimestral: 54.90,
    anual: 174.90
  };

  // Fetch referrals data
  const fetchReferrals = async () => {
    if (!user?.id) return;
    
    setLoadingReferrals(true);
    try {
      const { data, error } = await supabase.rpc('get_affiliate_referrals', {
        p_user_id: user.id
      });

      if (error) {
        console.error('Erro ao buscar indicados:', error);
        return;
      }

      setReferrals(data || []);
    } catch (error) {
      console.error('Erro ao buscar indicados:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  useEffect(() => {
    if (affiliateCode && user?.id) {
      fetchReferrals();
    }
  }, [affiliateCode, user?.id]);
  
  const calculateCommission = (sales: number, plan: string) => {
    const price = planPrices[plan as keyof typeof planPrices];
    return (price * sales * 0.20); // 20% de comissão
  };

  const copyAffiliateLink = () => {
    if (!affiliateCode) return;
    
    const affiliateLink = `${window.location.origin}?ref=${affiliateCode.code}`;
    navigator.clipboard.writeText(affiliateLink);
    toast.success("Link de afiliado copiado!");
  };

  if (!affiliateCode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Sistema de Afiliados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <div className="text-muted-foreground space-y-2">
              <p className="font-medium">Torne-se um afiliado e ganhe 20% de comissão em cada venda!</p>
              <ul className="text-sm space-y-1">
                <li>• Compartilhe seu link personalizado</li>
                <li>• Ganhe 20% em cada venda confirmada</li>
                <li>• Acompanhe suas vendas em tempo real</li>
                <li>• Pagamentos mensais automáticos</li>
              </ul>
            </div>
            <Button 
              onClick={createAffiliateCode}
              disabled={isLoading}
              className="bg-green-500 hover:bg-green-600"
            >
              {isLoading ? "Criando..." : "Criar Código de Afiliado"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Ganho</p>
                <p className="text-lg font-bold">R$ {affiliateCode.total_earnings.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Vendas</p>
                <p className="text-lg font-bold">{affiliateCode.total_sales}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Comissão</p>
                <p className="text-lg font-bold">{affiliateCode.commission_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Código de Afiliado */}
      <Card>
        <CardHeader>
          <CardTitle>Seu Código de Afiliado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="text-lg px-4 py-2">
              {affiliateCode.code}
            </Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={copyAffiliateLink}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copiar Link
            </Button>
          </div>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Compartilhe seu link de afiliado e ganhe 20% de comissão em cada venda realizada!</p>
            <div className="bg-muted/30 p-3 rounded text-xs">
              <strong>Como funciona:</strong>
              <br />1. Compartilhe seu link personalizado
              <br />2. Quando alguém comprar usando seu link, você ganha 20%
              <br />3. Acompanhe suas vendas na seção "Histórico de Vendas"
              <br />4. Receba seus pagamentos mensalmente
            </div>
            
            {/* Previsão de Ganhos Mensais */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mt-4">
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center gap-2">
                ⚠️ Previsão de ganhos mensais
              </h4>
              
              {/* Plano Mensal */}
              <div className="mb-4">
                <h5 className="font-medium text-sm mb-2">Plano Mensal • Preço R$ 24,90</h5>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-medium">5 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 24,90</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">10 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 49,80</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">20 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 99,60</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">50 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 249,00</div>
                  </div>
                </div>
              </div>

              {/* Plano Trimestral */}
              <div className="mb-4">
                <h5 className="font-medium text-sm mb-2">Plano Trimestral • Preço R$ 54,90</h5>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-medium">5 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 54,90</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">10 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 109,80</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">20 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 219,60</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">50 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 549,00</div>
                  </div>
                </div>
              </div>

              {/* Plano Anual */}
              <div className="mb-4">
                <h5 className="font-medium text-sm mb-2">Plano Anual • Preço R$ 174,90</h5>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-medium">5 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 174,90</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">10 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 349,80</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">20 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 699,60</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium">50 vendas:</div>
                    <div className="text-green-600 font-bold">R$ 1.749,00</div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-3 italic">
                Calcule sua conta com 20% de comissão e nos preços atuais dos planos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculadora de Comissões */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-500" />
            Calculadora de Comissões
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="calc-sales">Número de Vendas</Label>
              <Input
                id="calc-sales"
                type="number"
                min="1"
                value={calcSales}
                onChange={(e) => setCalcSales(Number(e.target.value))}
                placeholder="Ex: 10"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="calc-plan">Tipo de Plano</Label>
              <Select value={calcPlan} onValueChange={setCalcPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o plano" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal (R$ 24,90)</SelectItem>
                  <SelectItem value="trimestral">Trimestral (R$ 54,90)</SelectItem>
                  <SelectItem value="anual">Anual (R$ 174,90)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Sua Comissão (20%)</Label>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  R$ {calculateCommission(calcSales, calcPlan).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Total em vendas: R$ {(planPrices[calcPlan as keyof typeof planPrices] * calcSales).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Dica:</strong> Com {calcSales} venda{calcSales > 1 ? 's' : ''} do plano {calcPlan}, 
              você ganhará <strong>R$ {calculateCommission(calcSales, calcPlan).toFixed(2)}</strong> em comissões!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Referrals List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Seus Indicados ({referrals.length})
          </CardTitle>
          <CardDescription>
            Lista de usuários que você indicou para a plataforma
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingReferrals ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando indicados...
            </div>
          ) : referrals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Você ainda não tem indicados.</p>
              <p className="text-sm">Compartilhe seu link de afiliado para começar a ganhar!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {referrals.map((referral) => (
                <div key={referral.customer_id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-accent/5 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{referral.customer_name || 'Usuário'}</p>
                        <p className="text-sm text-muted-foreground">{referral.customer_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                      <Badge variant={referral.customer_is_subscribed ? 'default' : 'secondary'}>
                        {referral.customer_is_subscribed ? 'Assinante' : 'Gratuito'}
                      </Badge>
                      <Badge variant={referral.status === 'completed' ? 'default' : referral.status === 'lead' ? 'outline' : 'secondary'}>
                        {referral.status === 'lead' ? 'Cadastrado' : referral.status === 'completed' ? 'Convertido' : referral.status}
                      </Badge>
                      <span className="text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {referral.customer_whatsapp && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const whatsappUrl = `https://wa.me/55${referral.customer_whatsapp.replace(/\D/g, '')}?text=Olá! Vi que você se cadastrou na nossa plataforma através da minha indicação. Como posso te ajudar?`;
                          window.open(whatsappUrl, '_blank');
                        }}
                        className="flex items-center gap-1"
                      >
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    )}
                    
                    <div className="text-right">
                      {referral.sale_amount > 0 && (
                        <>
                          <p className="font-medium">R$ {referral.sale_amount.toFixed(2)}</p>
                          <p className="text-sm text-green-600">
                            R$ {referral.commission_amount.toFixed(2)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliatePanel;
